import React from 'react';
import {
  Briefcase,
  Users,
  Heart,
  TrendingUp,
  MapPin,
  Clock,
} from 'lucide-react';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
  MarketingPrimaryButton,
  MarketingHeroLightButton,
  MarketingGhostOnDark,
} from '../../components/home/MarketingPageChrome';

const CAREERS_HERO =
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1800&q=80';

const Careers = () => {
  const values = [
    {
      icon: Users,
      title: 'Collaborative culture',
      description: 'Work with people who care about making events memorable.',
    },
    {
      icon: TrendingUp,
      title: 'Growth opportunities',
      description: 'Learning, mentorship, and clear paths to advance.',
    },
    {
      icon: Heart,
      title: 'Work-life balance',
      description: 'Sustainable practices and flexible arrangements.',
    },
    {
      icon: Briefcase,
      title: 'Real impact',
      description: 'Help connect people with exceptional event experiences.',
    },
  ];

  const openPositions = [
    {
      title: 'Senior Full-Stack Developer',
      department: 'Engineering',
      location: 'Remote / Accra, Ghana',
      type: 'Full-time',
      description:
        'Build and scale our platform using modern technologies. Lead initiatives and mentor juniors.',
    },
    {
      title: 'Product Designer',
      department: 'Design',
      location: 'Remote / Accra, Ghana',
      type: 'Full-time',
      description:
        'Design calm, intuitive experiences. Work closely with product and engineering.',
    },
    {
      title: 'Customer Success Manager',
      department: 'Operations',
      location: 'Accra, Ghana',
      type: 'Full-time',
      description: 'Help clients and vendors succeed. Build relationships and drive satisfaction.',
    },
    {
      title: 'Marketing Specialist',
      department: 'Marketing',
      location: 'Remote / Accra, Ghana',
      type: 'Full-time',
      description: 'Grow our user base and brand with clear, thoughtful campaigns.',
    },
    {
      title: 'Business Development Representative',
      department: 'Sales',
      location: 'Accra, Ghana',
      type: 'Full-time',
      description: 'Onboard vendors and expand the marketplace through partnerships.',
    },
    {
      title: 'Data Analyst',
      department: 'Analytics',
      location: 'Remote',
      type: 'Full-time',
      description: 'Turn platform data into insights that guide product and growth.',
    },
  ];

  const benefits = [
    { icon: Heart, title: 'Health coverage', description: 'Medical benefits for you and dependents.' },
    { icon: Clock, title: 'Flexible work', description: 'Remote-friendly schedules that respect life outside work.' },
    { icon: TrendingUp, title: 'Learning budget', description: 'Courses, conferences, and tools to grow.' },
    { icon: Users, title: 'Equity & PTO', description: 'Competitive pay, equity, and generous time off.' },
  ];

  const applyHref = (title) =>
    `/contact?subject=${encodeURIComponent(`Careers: ${title}`)}`;

  return (
    <MarketingPage>
      <MarketingMediaHero
        title="Join our team"
        subtitle="Our people are the heart of VendaList. Build the marketplace that helps events come to life."
        image={CAREERS_HERO}
        actions={
          <>
            <MarketingHeroLightButton href="#open-positions">
              View open positions
            </MarketingHeroLightButton>
            <MarketingGhostOnDark to="/about">About VendaList</MarketingGhostOnDark>
          </>
        }
      />

      <MarketingSection tone="white">
        <MarketingSplitIntro title="Work that shapes celebrations">
          <p>
            We’re connecting clients with exceptional vendors — and building a healthy culture where
            people are excited to show up and make an impact.
          </p>
          <p>
            If that sounds like you, get in touch. We’d love to talk.
          </p>
        </MarketingSplitIntro>
      </MarketingSection>

      <MarketingSection tone="white" className="!pt-0 sm:!pt-0">
        <MarketingFeatureGrid items={values} />
      </MarketingSection>

      <MarketingSection tone="canvas" id="open-positions">
        <div className="mx-auto max-w-3xl">
          <MarketingReveal className="mb-10">
            <h2 className="marketing-section-title mb-3" style={{ color: '#0a0a0a' }}>
              Open positions
            </h2>
            <p className="text-[1.05rem] leading-relaxed" style={{ color: '#6b6b6b' }}>
              Don’t see a perfect fit? Reach out anyway — we’re always curious about great people.
            </p>
          </MarketingReveal>

          <div className="divide-y divide-m-border-light border-y border-m-border-light">
            {openPositions.map((role) => (
              <MarketingReveal key={role.title} className="py-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p
                      className="mb-1 text-[11px] font-medium uppercase tracking-[0.14em]"
                      style={{ color: '#8a8a8a' }}
                    >
                      {role.department}
                    </p>
                    <h3 className="mb-2 text-lg font-bold tracking-tight" style={{ color: '#0a0a0a' }}>
                      {role.title}
                    </h3>
                    <p className="mb-3 text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
                      {role.description}
                    </p>
                    <div className="flex flex-wrap gap-4 text-xs" style={{ color: '#8a8a8a' }}>
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {role.location}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {role.type}
                      </span>
                    </div>
                  </div>
                  <MarketingPrimaryButton to={applyHref(role.title)} className="shrink-0">
                    Apply
                  </MarketingPrimaryButton>
                </div>
              </MarketingReveal>
            ))}
          </div>
        </div>
      </MarketingSection>

      <MarketingSection tone="white">
        <div className="mx-auto max-w-6xl">
          <MarketingReveal className="mb-10 max-w-xl">
            <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
              Benefits
            </h2>
          </MarketingReveal>
          <MarketingFeatureGrid items={benefits} />
        </div>
      </MarketingSection>

      <MarketingSection tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            Ready to build with us?
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">
            Tell us what you’re great at — we’ll find the right conversation.
          </p>
          <MarketingPrimaryButton
            to="/contact?subject=Careers"
            className="!bg-white !text-m-ink hover:!bg-white/90"
          >
            Get in touch
          </MarketingPrimaryButton>
        </MarketingReveal>
      </MarketingSection>
    </MarketingPage>
  );
};

export default Careers;
